import axiosInstance from '../axiosClient';

/**
 * Organization File API
 * Handles file operations for organization-wide shared storage
 */

/**
 * Check organization storage availability
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @returns {Promise<Object>} Storage info
 */
export const checkOrganizationStorageAvailability = async (token, orgUuid) => {
    const response = await axiosInstance.get(
        `/organization/${orgUuid}/missionlog/storage`,
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data;
};

/**
 * Generate presigned URL for organization file upload
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {string} fileName - File name
 * @param {number} fileSize - File size in bytes
 * @param {string} fileType - MIME type
 * @param {number} operationId - Operation ID
 * @returns {Promise<Object>} Upload details
 */
export const generateOrganizationPresignUrl = async (token, fileName, fileSize, fileType, orgUuid, operationId) => {
    const response = await axiosInstance.post(
        `/organization/${orgUuid}/missionlog/presigned-url`,
        {
            fileName,
            fileSize,
            fileType,
            operationId
        },
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data;
};

/**
 * Complete multipart upload for organization file
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {number} operationId - Operation ID
 * @param {string} fileName - File name
 * @param {string} uploadId - Upload ID
 * @param {Array} parts - Uploaded parts with ETags
 * @returns {Promise<Object>}
 */
export const completeOrganizationMultipartUpload = async (token, orgUuid, operationId, fileName, uploadId, parts) => {
    const response = await axiosInstance.post(
        `/organization/${orgUuid}/missionlog/complete-multipart`,
        {
            operationId,
            fileName,
            uploadId,
            parts
        },
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data;
};

/**
 * Abort multipart upload for organization file
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {number} operationId - Operation ID
 * @param {string} fileName - File name
 * @param {string} uploadId - Upload ID
 * @returns {Promise<Object>}
 */
export const abortOrganizationMultipartUpload = async (token, orgUuid, operationId, fileName, uploadId) => {
    const response = await axiosInstance.post(
        `/organization/${orgUuid}/missionlog/abort-multipart`,
        {
            operationId,
            fileName,
            uploadId
        },
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data;
};

/**
 * Get all files for organization operation
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {number} operationId - Operation ID
 * @returns {Promise<Array>} List of files
 */
export const getAllOrganizationFiles = async (token, orgUuid, operationId) => {
    const response = await axiosInstance.get(
        `/organization/${orgUuid}/missionlog/operations/${operationId}/files`,
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data.files;
};

/**
 * Generate download URL for organization file
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {string} fileName - File name
 * @param {string} fileType - MIME type
 * @param {number} operationId - Operation ID
 * @returns {Promise<string>} Download URL
 */
export const getOrganizationDownloadUrl = async (token, orgUuid, fileName, fileType, operationId) => {
    const response = await axiosInstance.post(
        `/organization/${orgUuid}/missionlog/download-url`,
        {
            operationId,
            fileName,
            fileType
        },
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data.downloadUrl;
};

/**
 * Delete organization file
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {number} operationId - Operation ID
 * @param {string} fileName - File name
 * @returns {Promise<Object>}
 */
export const deleteOrganizationFile = async (token, orgUuid, operationId, fileName) => {
    const response = await axiosInstance.delete(
        `/organization/${orgUuid}/missionlog/files`,
        {
            data: {
                operationId,
                fileName
            },
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data;
};

/**
 * Check if organization file exists
 * @param {string} token - Auth token
 * @param {string} orgUuid - Organization UUID
 * @param {string} fileName - File name
 * @param {number} operationId - Operation ID
 * @returns {Promise<boolean>} True if file exists
 */
export const checkOrganizationFileNameExists = async (token, orgUuid, fileName, operationId) => {
    const response = await axiosInstance.get(
        `/organization/${orgUuid}/missionlog/check-exists/${operationId}/${fileName}`,
        {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }
    );
    return response.data.exists;
};
